import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Icon } from '@bloo-team/ui'
import { Search } from '@bloo-team/ui/icons'
import { formatBytes, formatCompactCount, type EnrichedStream } from '@bloo-team/core-shell'

/**
 * Storage · Capacity — the streams as a volume ledger (docs/storage-requirements.md
 * §Capacity tab). Rows are the Streams page's `enrich` output, already carrying
 * the demo retention overlay (`applyRetentionOverlay`), so the two pages never
 * disagree on a number.
 *
 * The share bar is RELATIVE TO THE LARGEST ROW, not to a quota: there is no
 * denominator until CB-32 closes, and a bar against an invented total would
 * read as headroom. "Today" is the daily-stream-details COUNT (a running total,
 * not bytes — `streams.ts` header note). No ratio between the three byte fields
 * is shown as compression (they are not derivable from one another).
 */

export type CapacitySortKey = 'stream' | 'size' | 'logical' | 'events' | 'retention' | 'replication'
export type CapacityChip = 'largest' | 'no-retention' | 'replicated'
export const isCapacityChip = (v: unknown): v is CapacityChip => v === 'largest' || v === 'no-retention' || v === 'replicated'

const CHIPS: Array<{ key: CapacityChip; label: string }> = [
  { key: 'largest', label: 'largest 10' },
  { key: 'no-retention', label: 'no retention' },
  { key: 'replicated', label: 'replication > 1' },
]

/** Query for the stream's last day in the search dialect (docs/sql-dialect.md). */
export const streamSearchQuery = (stream: string) => `stream=${stream} | duration 24h`

const num = (r: EnrichedStream, k: CapacitySortKey): number => {
  switch (k) {
    case 'size': return r.stream.size_on_disk
    case 'logical': return r.stream.total_len
    case 'events': return r.eventsToday ?? -1
    case 'retention': return r.stream.retention ?? -1
    case 'replication': return r.stream.replication ?? -1
    default: return 0
  }
}

export function sortCapacity(rows: EnrichedStream[], key: CapacitySortKey, dir: 'asc' | 'desc'): EnrichedStream[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    if (key === 'stream') return sign * a.stream.stream.localeCompare(b.stream.stream)
    const d = num(a, key) - num(b, key)
    return d !== 0 ? sign * d : a.stream.stream.localeCompare(b.stream.stream)
  })
}

/** Which rows a chip keeps. `largest` is by on-disk size over the FULL set. */
export function chipFilter(rows: EnrichedStream[], chip: CapacityChip | null): EnrichedStream[] {
  if (chip === null) return rows
  if (chip === 'no-retention') return rows.filter((r) => r.stream.retention === null)
  if (chip === 'replicated') return rows.filter((r) => (r.stream.replication ?? 0) > 1)
  const top = new Set(
    [...rows].sort((a, b) => b.stream.size_on_disk - a.stream.size_on_disk).slice(0, 10).map((r) => r.stream.stream),
  )
  return rows.filter((r) => top.has(r.stream.stream))
}

function SortTh({
  k, label, num: isNum, sortKey, sortDir, onSort,
}: {
  k: CapacitySortKey
  label: string
  num?: boolean
  sortKey: CapacitySortKey
  sortDir: 'asc' | 'desc'
  onSort: (k: CapacitySortKey) => void
}) {
  const active = sortKey === k
  return (
    <th
      className={isNum ? 'num' : undefined}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="th-sort" onClick={() => onSort(k)}>
        {label}
        <span className="th-sort-mark" aria-hidden="true">{active ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  )
}

export interface CapacityTabProps {
  clusterId: string
  scopeId: string
  rows: EnrichedStream[]
  isPending: boolean
  isError: boolean
  error: unknown
  /** The URL's `?chip=` (Overview pivots land pre-filtered); re-seeds the chip when it changes. */
  chip?: CapacityChip
  /** Called with the chip a click leaves pressed (null when toggled off), so the page writes the URL. */
  onChipChange?: (chip: CapacityChip | null) => void
}

export function CapacityTab({ clusterId, scopeId, rows, isPending, isError, error, chip: chipParam, onChipChange }: CapacityTabProps) {
  const p = (to: string) => `/${clusterId}/${scopeId}${to}` as string
  const [chip, setChipState] = useState<CapacityChip | null>(chipParam ?? null)
  // The URL owns the chip: back/forward or a pivot re-seeds it (derived state during render, as StreamsPage does for ?open=).
  const [seenParam, setSeenParam] = useState(chipParam)
  if (seenParam !== chipParam) {
    setSeenParam(chipParam)
    setChipState(chipParam ?? null)
  }
  const setChip = (next: CapacityChip | null) => {
    setChipState(next)
    onChipChange?.(next)
  }
  const [sortKey, setSortKey] = useState<CapacitySortKey>('size')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const counts = useMemo(
    () => ({
      largest: Math.min(10, rows.length),
      'no-retention': rows.filter((r) => r.stream.retention === null).length,
      replicated: rows.filter((r) => (r.stream.replication ?? 0) > 1).length,
    }),
    [rows],
  )
  const visible = useMemo(() => sortCapacity(chipFilter(rows, chip), sortKey, sortDir), [rows, chip, sortKey, sortDir])
  // The bar's denominator: the biggest stream in the whole set, not the filtered one.
  const largest = useMemo(() => rows.reduce((m, r) => Math.max(m, r.stream.size_on_disk), 0), [rows])

  const toggleSort = (key: CapacitySortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(key === 'stream' ? 'asc' : 'desc')
    }
  }

  return (
    <div className="hud-stack" role="tabpanel" aria-label="Capacity">
      <div className="hud-row" style={{ gap: 'var(--bloo-space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className="list-chip"
            aria-pressed={chip === c.key}
            onClick={() => setChip(chip === c.key ? null : c.key)}
          >
            {c.label} <span className="n">{counts[c.key]}</span>
          </button>
        ))}
        <span className="hud-unit" style={{ marginLeft: 'auto' }}>share bar is relative to the largest stream — no quota (CB-32)</span>
      </div>

      {isError ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>
            <span className="bui-alert-title">Could not load streams</span>
            {error instanceof Error ? error.message : 'Unknown error.'}
          </span>
        </div>
      ) : null}
      {isPending ? <p className="hud-unit" role="status">Loading streams…</p> : null}
      {!isPending && !isError && visible.length === 0 ? (
        <p className="hud-unit" role="status">No streams match this filter.</p>
      ) : null}

      {visible.length > 0 ? (
        <div className="dense-table-wrap">
          <table className="dense-table st-table">
            <thead>
              <tr>
                <SortTh k="stream" label="Stream" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh k="size" label="On disk" num sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh k="logical" label="Logical" num sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh k="events" label="Today" num sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh k="retention" label="Retention" num sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh k="replication" label="Replication" num sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const s = r.stream
                const share = largest > 0 ? Math.max(1, Math.round((s.size_on_disk / largest) * 100)) : 0
                return (
                  <tr key={s.stream} data-severity={s.retention === null ? 'low' : 'info'}>
                    <td>
                      <Link
                        to={p('/fabric/streams')}
                        search={{ open: s.stream } as Record<string, string>}
                        className="cell-name"
                        aria-label={`Open ${s.stream} on Streams`}
                      >
                        {s.stream}
                      </Link>
                    </td>
                    <td className="num st-disk">
                      <span className="st-share" aria-hidden="true">
                        <span style={{ width: `${share}%` }} />
                      </span>
                      <span>{formatBytes(s.size_on_disk)}</span>
                    </td>
                    <td className="num">{formatBytes(s.total_len)}</td>
                    <td className="num" title="events so far today — a count, not bytes">
                      {r.eventsToday === undefined ? '—' : formatCompactCount(r.eventsToday)}
                    </td>
                    <td className="num">{s.retention === null ? '—' : `${s.retention}d`}</td>
                    <td className="num">{s.replication ?? '—'}</td>
                    <td>
                      <span className="row-actions">
                        <Link
                          to={p('/fabric/search')}
                          search={{ q: streamSearchQuery(s.stream) } as Record<string, string>}
                          className="row-action"
                          aria-label={`Search events in ${s.stream}`}
                        >
                          <Icon icon={Search} size={13} />
                        </Link>
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
